import { useCallback, useMemo, useRef, useState } from "react";

import {
    EMPTY_SELECTION,
    selectedKeysForPage,
    selectionAfterHeaderToggle,
    selectionAfterRowToggle,
    selectionCapError,
    selectionCount,
    selectionRequestFields,
    selectionRowIDs,
} from "../selectionHelpers";

// Row selection with a select-all-across-pages mode, shared by the Assets tab
// and the Network tab. Every rule lives in ../selectionHelpers.js; this hook
// only holds the state and hands GenericDraggableTableView the props it takes.
//
// @param {Object}   options
// @param {Array}    options.records  The rows on the page on screen.
// @param {number}   options.total    The filtered total, for the count.
// @param {Function} [options.toRow]  What to keep of a ticked record. Must
//   return an object with `id`. Defaults to `{ id }`.
const useSelectAllAcrossPages = ({ records = [], total = 0, toRow } = {}) => {
    const [selection, setSelection] = useState(EMPTY_SELECTION);

    // Read through a ref so a caller's inline mapper does not rebuild every
    // handler on every render.
    const toRowRef = useRef(toRow);
    toRowRef.current = toRow;
    const mapper = useCallback(
        (record) => (toRowRef.current ? toRowRef.current(record) : { id: record.id }),
        []
    );

    const clearSelection = useCallback(() => setSelection(EMPTY_SELECTION), []);

    const onSelect = useCallback(
        (record, selected) =>
            setSelection((prev) =>
                selectionAfterRowToggle(prev, record, selected, {
                    pageRecords: records,
                    toRow: mapper,
                })
            ),
        [records, mapper]
    );

    // antd: onSelectAll(selected, selectedRows, changeRows).
    const onSelectAll = useCallback(
        (selected, _selectedRows, changeRows) =>
            setSelection((prev) =>
                selectionAfterHeaderToggle(prev, selected, {
                    pageRecords: records,
                    changeRows,
                    toRow: mapper,
                })
            ),
        [records, mapper]
    );

    const selectedRowKeys = useMemo(
        () => selectedKeysForPage(selection, records),
        [selection, records]
    );

    const count = selectionCount(selection, total);
    const rowIDs = useMemo(() => selectionRowIDs(selection), [selection]);
    const requestFields = useMemo(() => selectionRequestFields(selection), [selection]);
    // Non-empty when hand-ticked rows exceed the backend's resourceIDs cap;
    // callers toast it and skip the request.
    const capError = useMemo(() => selectionCapError(selection), [selection]);

    return {
        isSelectAll: selection.isSelectAll,
        // The explicitly ticked rows (in select-all mode: the page the header
        // was ticked on). Not "everything selected" -- use isSelectAll for that.
        selectedRows: selection.rows,
        count,
        requestFields,
        capError,
        clearSelection,
        rowSelection: { selectedRowKeys, onSelect, onSelectAll },
        getTotalRowSelection: () => rowIDs,
        getTotalRowSelectionCount: () => count,
    };
};

export default useSelectAllAcrossPages;
